// ===== Production Logger Utility =====

import { LOG_LEVELS, STORAGE_KEYS } from '../constants/ChatConstants';

export class Logger {
  constructor(context = 'ChatApp', level = LOG_LEVELS.INFO) {
    this.context = context;
    this.level = process.env.NODE_ENV === 'development' ? LOG_LEVELS.DEBUG : level;
    this.logs = [];
    this.maxLogs = 1000; // Keep only last 1000 logs
    this.enableConsole = true;
    this.enableStorage = process.env.NODE_ENV === 'development';
    
    // Performance monitoring
    this.performanceMarks = new Map();
    
    // Error tracking
    this.errorCount = 0;
    this.lastError = null;
    
    this.init();
  }

  init() {
    // Load existing logs from storage if enabled
    if (this.enableStorage) {
      try {
        const storedLogs = localStorage.getItem(STORAGE_KEYS.DEBUG_LOGS);
        if (storedLogs) {
          this.logs = JSON.parse(storedLogs).slice(-this.maxLogs);
        }
      } catch (error) {
        console.warn('Failed to load stored logs:', error);
      }
    }

    // Set up global error handlers in development
    if (process.env.NODE_ENV === 'development') {
      window.addEventListener('error', (event) => {
        this.error('Global Error:', event.error);
      });

      window.addEventListener('unhandledrejection', (event) => {
        this.error('Unhandled Promise Rejection:', event.reason);
      });
    }
  }

  /**
   * Create log entry
   */
  createLogEntry(level, message, data = null) {
    const timestamp = new Date().toISOString();
    const entry = {
      timestamp,
      level,
      context: this.context,
      message: typeof message === 'string' ? message : JSON.stringify(message),
      data: data ? (typeof data === 'object' ? JSON.stringify(data) : data) : null,
      stack: level === LOG_LEVELS.ERROR ? new Error().stack : null
    };

    return entry;
  }

  /**
   * Add log entry to storage
   */
  addLogEntry(entry) {
    this.logs.push(entry);
    
    // Keep only recent logs
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }

    // Save to localStorage if enabled
    if (this.enableStorage) {
      try {
        localStorage.setItem(STORAGE_KEYS.DEBUG_LOGS, JSON.stringify(this.logs));
      } catch (error) {
        // Storage might be full, clear old logs
        this.logs = this.logs.slice(-500);
        try {
          localStorage.setItem(STORAGE_KEYS.DEBUG_LOGS, JSON.stringify(this.logs));
        } catch (e) {
          console.warn('Failed to save logs to storage:', e);
        }
      }
    }
  }

  /**
   * Output to console
   */
  outputToConsole(level, entry) {
    if (!this.enableConsole) return;

    const prefix = `[${entry.timestamp}] [${this.context}]`;
    const message = entry.data ? `${entry.message} ${entry.data}` : entry.message;

    switch (level) {
      case LOG_LEVELS.ERROR:
        console.error(prefix, message);
        break;
      case LOG_LEVELS.WARN:
        console.warn(prefix, message);
        break;
      case LOG_LEVELS.INFO:
        console.info(prefix, message);
        break;
      case LOG_LEVELS.DEBUG:
        console.debug(prefix, message);
        break;
      case LOG_LEVELS.TRACE:
        console.trace(prefix, message);
        break;
      default:
        console.log(prefix, message);
    }
  }

  /**
   * Log error message
   */
  error(message, data = null) {
    if (this.level < LOG_LEVELS.ERROR) return;
    
    const entry = this.createLogEntry(LOG_LEVELS.ERROR, message, data);
    this.addLogEntry(entry);
    this.outputToConsole(LOG_LEVELS.ERROR, entry);
    
    this.errorCount++;
    this.lastError = { message, data, timestamp: new Date() };

    // Report to external service in production
    if (process.env.NODE_ENV === 'production' && window.errorReporting) {
      window.errorReporting.captureException(new Error(message), {
        context: this.context,
        data
      });
    }
  }

  /**
   * Log warning message
   */
  warn(message, data = null) {
    if (this.level < LOG_LEVELS.WARN) return;
    
    const entry = this.createLogEntry(LOG_LEVELS.WARN, message, data);
    this.addLogEntry(entry);
    this.outputToConsole(LOG_LEVELS.WARN, entry);
  }

  /**
   * Log info message
   */
  info(message, data = null) {
    if (this.level < LOG_LEVELS.INFO) return;
    
    const entry = this.createLogEntry(LOG_LEVELS.INFO, message, data);
    this.addLogEntry(entry);
    this.outputToConsole(LOG_LEVELS.INFO, entry);
  }

  /**
   * Log debug message
   */
  debug(message, data = null) {
    if (this.level < LOG_LEVELS.DEBUG) return;
    
    const entry = this.createLogEntry(LOG_LEVELS.DEBUG, message, data);
    this.addLogEntry(entry);
    this.outputToConsole(LOG_LEVELS.DEBUG, entry);
  }

  /**
   * Log trace message
   */
  trace(message, data = null) {
    if (this.level < LOG_LEVELS.TRACE) return;
    
    const entry = this.createLogEntry(LOG_LEVELS.TRACE, message, data);
    this.addLogEntry(entry);
    this.outputToConsole(LOG_LEVELS.TRACE, entry);
  }

  /**
   * Performance monitoring
   */
  startTimer(label) {
    this.performanceMarks.set(label, performance.now());
    this.debug(`Timer started: ${label}`);
  }

  endTimer(label) {
    const startTime = this.performanceMarks.get(label);
    if (startTime) {
      const duration = performance.now() - startTime;
      this.performanceMarks.delete(label);
      this.info(`Timer ${label}: ${duration.toFixed(2)}ms`);
      return duration;
    }
    this.warn(`Timer ${label} not found`);
    return 0;
  }

  /**
   * Group logging
   */
  group(label) {
    if (this.enableConsole) {
      console.group(`[${this.context}] ${label}`);
    }
  }

  groupEnd() {
    if (this.enableConsole) {
      console.groupEnd();
    }
  }

  /**
   * Log network request
   */
  logRequest(method, url, data = null) {
    this.debug(`${method.toUpperCase()} ${url}`, data);
  }

  /**
   * Log network response
   */
  logResponse(method, url, status, data = null) {
    const level = status >= 400 ? LOG_LEVELS.ERROR : LOG_LEVELS.DEBUG;
    const message = `${method.toUpperCase()} ${url} - ${status}`;
    
    if (level === LOG_LEVELS.ERROR) {
      this.error(message, data);
    } else {
      this.debug(message, data);
    }
  }

  /**
   * Log WebRTC events
   */
  logWebRTC(event, data = null) {
    this.info(`WebRTC: ${event}`, data);
  }

  /**
   * Log Socket events
   */
  logSocket(event, data = null) {
    this.debug(`Socket: ${event}`, data);
  }

  /**
   * Get logs for debugging
   */
  getLogs(filter = null) {
    if (!filter) return this.logs;
    
    return this.logs.filter(log => {
      if (filter.level && log.level < filter.level) return false;
      if (filter.context && !log.context.includes(filter.context)) return false;
      if (filter.message && !log.message.includes(filter.message)) return false;
      if (filter.since && new Date(log.timestamp) < filter.since) return false;
      return true;
    });
  }

  /**
   * Export logs
   */
  exportLogs() {
    const data = {
      context: this.context,
      exportTime: new Date().toISOString(),
      errorCount: this.errorCount,
      lastError: this.lastError,
      logs: this.logs
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { 
      type: 'application/json' 
    });
    
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chat-logs-${this.context}-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /**
   * Clear logs
   */
  clearLogs() {
    this.logs = [];
    this.errorCount = 0;
    this.lastError = null;
    
    if (this.enableStorage) {
      try {
        localStorage.removeItem(STORAGE_KEYS.DEBUG_LOGS);
      } catch (error) {
        this.warn('Failed to clear stored logs:', error);
      }
    }
    
    this.info('Logs cleared');
  }

  /**
   * Get logger stats
   */
  getStats() {
    const levels = {};
    this.logs.forEach(log => {
      levels[log.level] = (levels[log.level] || 0) + 1;
    });

    return {
      totalLogs: this.logs.length,
      errorCount: this.errorCount,
      lastError: this.lastError,
      levelBreakdown: levels,
      context: this.context,
      level: this.level
    };
  }

  /**
   * Set log level
   */
  setLevel(level) {
    this.level = level;
    this.info(`Log level set to: ${level}`);
  }

  /**
   * Enable/disable console output
   */
  setConsoleOutput(enabled) {
    this.enableConsole = enabled;
    this.info(`Console output ${enabled ? 'enabled' : 'disabled'}`);
  }

  /**
   * Enable/disable storage
   */
  setStorageEnabled(enabled) {
    this.enableStorage = enabled;
    this.info(`Storage ${enabled ? 'enabled' : 'disabled'}`);
  }
}

// Global logger instance
export const globalLogger = new Logger('Global');

// Convenience functions
export const log = {
  error: (message, data) => globalLogger.error(message, data),
  warn: (message, data) => globalLogger.warn(message, data),
  info: (message, data) => globalLogger.info(message, data),
  debug: (message, data) => globalLogger.debug(message, data),
  trace: (message, data) => globalLogger.trace(message, data)
};

export default Logger;
